import { useEffect, useRef, useState } from 'react';

// How long a link to a document stays valid, in hours. 0 means it never expires.
// The default for a workspace is set in Settings -> Users & Security (GET /settings/link-expiry);
// every send screen starts from it and lets the sender pick another for that send.
export const NO_EXPIRY = 0;
export const FALLBACK_EXPIRY_HOURS = 72;

export const LINK_EXPIRY_OPTIONS = [
  { hours: 24, label: '24 hours' },
  { hours: 48, label: '48 hours' },
  { hours: 72, label: '72 hours (3 days)' },
  { hours: 168, label: '1 week' },
  { hours: 336, label: '2 weeks' },
  { hours: 720, label: '30 days' },
  { hours: NO_EXPIRY, label: 'No expiry' },
];

/** Plain-English length of a lifetime in hours, e.g. 72 -> "3 days". */
export function linkExpiryLabel(hours) {
  if (hours === NO_EXPIRY) return 'No expiry';
  if (hours % 168 === 0) return hours === 168 ? '1 week' : `${hours / 168} weeks`;
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/** The standard choices, plus `current` if it's something else (an unusual saved default). */
export function linkExpiryOptions(current) {
  if (current == null || LINK_EXPIRY_OPTIONS.some((o) => o.hours === current)) return LINK_EXPIRY_OPTIONS;
  return [...LINK_EXPIRY_OPTIONS, { hours: current, label: linkExpiryLabel(current) }]
    .sort((a, b) => (a.hours === NO_EXPIRY ? 1 : b.hours === NO_EXPIRY ? -1 : a.hours - b.hours));
}

/**
 * State for a send screen's "Link expiry" choice, starting from the workspace default.
 * `fetchDefault` returns the axios promise for GET /settings/link-expiry (each caller has its own
 * client). Until it answers the choice is the 72-hour fallback; once the sender picks something
 * themselves, a late answer never overwrites it. A screen that only needs the default while a
 * dialog is open passes `enabled` = whether it is: every time it turns on, the choice starts
 * again from the current default.
 */
export function useLinkExpiry(fetchDefault, enabled = true) {
  const [hours, setHours] = useState(FALLBACK_EXPIRY_HOURS);
  const [defaultHours, setDefaultHours] = useState(null);
  const picked = useRef(false);
  const fetchRef = useRef(fetchDefault);
  fetchRef.current = fetchDefault;

  useEffect(() => {
    if (!enabled) return undefined;
    picked.current = false;
    let alive = true;
    Promise.resolve(fetchRef.current())
      .then(({ data }) => {
        const d = Number(data?.default_hours);
        if (!alive || !Number.isInteger(d) || d < 0) return;
        setDefaultHours(d);
        if (!picked.current) setHours(d);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [enabled]);

  const choose = (value) => {
    picked.current = true;
    setHours(value);
  };
  return { expiryHours: hours, setExpiryHours: choose, defaultHours };
}
